<script setup>
import { ref, onMounted } from "vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import AppDataTable from "@/components/AppDataTable.vue";
import { fetchFeaturedStamps, deleteFeaturedStamp } from "./api";
import { useRouter } from "vue-router";

const router = useRouter();
const columns = [
  { key: "featuredStamp", label: "Featured Stamp", sortable: true },
  { key: "bonusPoints",   label: "Bonus Points" },
  { key: "featuredOn",    label: "Featured On" },
  { key: "actions",       label: "Actions",        align: "end", width: "150px" },
];
const rows = ref([]);
const loading = ref(false);
const error = ref("");
const pendingDelete = ref(null);
const deleting = ref(false);

async function load() {
  loading.value = true; error.value = "";
  try {
    const list = await fetchFeaturedStamps();
    rows.value = list.map((s) => ({
      id: s.index,
      featuredStamp: s.featured_stamp ?? "—",
      bonusPoints: s.bonus_points ?? 0,
      featuredOn: s.displayRange || (Array.isArray(s.target_range) ? s.target_range.join(" ~ ") : "—"),
    }));
  }
  catch { error.value = "Couldn't load featured stamps."; }
  finally { loading.value = false; }
}

async function confirmDelete() {
  if (!pendingDelete.value) return;
  deleting.value = true;
  try { await deleteFeaturedStamp(pendingDelete.value.id); rows.value = rows.value.filter((r) => r.id !== pendingDelete.value.id); pendingDelete.value = null; }
  catch { error.value = "Couldn't delete."; }
  finally { deleting.value = false; }
}

onMounted(load);
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Featured Stamp</h1>
      <div class="d-flex gap-2">
        <VBtn variant="tonal" @click="load">Refresh</VBtn>
        <VBtn @click="router.push({ name: 'featured-stamp-new' })">Add Featured Stamp</VBtn>
      </div>
    </div>
    <VAlert v-if="error" type="error" closable class="mb-4" @click:close="error = ''">{{ error }}</VAlert>
    <AppDataTable :columns="columns" :rows="rows" :loading="loading" row-key="id" searchable search-placeholder="Search…" empty-text="No featured stamps yet.">
      <template #cell-actions="{ row }">
        <VBtn size="small" variant="text" @click.stop="router.push({ name: 'featured-stamp-edit', params: { id: row.id } })">Edit</VBtn>
        <VBtn size="small" color="error" variant="text" @click.stop="pendingDelete = row">Delete</VBtn>
      </template>
    </AppDataTable>
    <ConfirmDialog :model-value="pendingDelete !== null" title="Delete featured stamp?" message="Permanently delete this featured stamp?" :loading="deleting" @update:model-value="pendingDelete = null" @confirm="confirmDelete" />
  </section>
</template>
